import type { GuideTopic } from '../engine/types';

/**
 * Single source of the user-facing copy. Both the tours and the inline
 * FieldHelp read from here, so a wording change lands in both at once.
 */
export const GUIDE_TOPICS = {
  'panel.inicio.tournaments': {
    title: 'Tus torneos',
    body: 'Acá ves de un vistazo los torneos que tenés en curso y los que están tomando inscripciones. Desde cada uno entrás al detalle para editarlo, pausar las inscripciones o cargar resultados.',
  },
  'panel.inicio.mercadopago': {
    title: 'Conexión con MercadoPago',
    body: 'Vincular tu cuenta de MercadoPago es lo que te habilita a cobrar inscripciones con tarjeta. Sin esa conexión no vas a poder entrar a crear torneos.',
    tip: 'Es el primer paso: sin MercadoPago vinculado, el botón de crear torneo te devuelve al inicio.',
  },
  'wizard.basicInfo.name': {
    title: 'Nombre del torneo',
    body: 'Es el título que ven los jugadores en la app cuando buscan torneos, y muchas veces lo único que leen antes de abrir el detalle. Mínimo 3 caracteres.',
    tip: 'Poné club, categorías y fecha: "Roca Club — 6ta y 7ma — Finde largo".',
  },
  'wizard.basicInfo.description': {
    title: 'Descripción',
    body: 'Se muestra en el detalle del torneo. Es donde van tus reglas propias, los horarios tentativos y qué incluye la inscripción. Mínimo 10 caracteres.',
    tip: 'El botón "Aplicar plantilla" te deja una base armada que después editás.',
  },
  'wizard.basicInfo.prizes': {
    title: 'Premios',
    body: 'Es uno de los campos que más mueve la inscripción. Conviene ser concreto: montos, productos, y qué se lleva cada puesto. Mínimo 5 caracteres.',
  },
  'wizard.basicInfo.banner': {
    title: 'Banner',
    body: 'La imagen de portada del torneo en el listado de la app. Es opcional, pero un torneo con banner se destaca sobre los que no tienen.',
  },
  'wizard.fixture.fixtureType': {
    title: 'Estructura del fixture',
    body: 'Elegís si hay fase de grupos antes de la eliminatoria o si es eliminación directa desde el primer partido. Con grupos, todos juegan al menos los partidos de su zona; con eliminación directa, el que pierde queda afuera.',
    tip: 'Con grupos nadie viaja al club para jugar un solo partido.',
  },
  'wizard.fixture.format': {
    title: 'Tipo de torneo',
    body: 'Define la duración y la dinámica general. Corto es el clásico de fin de semana. Americano es de un día, con pareja fija que va rotando rivales. Liga todavía no está disponible.',
    tip: 'Para un primer torneo, Corto es lo más predecible de organizar.',
  },
  'wizard.fixture.groupsFixed': {
    title: 'Equipos por grupo y clasificados',
    body: 'Durante la creación quedan fijos en 3 y 2 para pádel, por eso los ves deshabilitados. Una vez creado el torneo los podés cambiar desde el detalle.',
  },
  'wizard.fixture.overflowGroupClassification': {
    title: 'Clasificados en grupos de 4',
    body: 'Cuando la cantidad de equipos no cierra justo con el tamaño de grupo, uno de los grupos queda más grande. Acá definís si ese grupo clasifica la misma cantidad que el resto o si suma un clasificado extra.',
  },
  'wizard.fixture.americanGroupFormat': {
    title: 'Formato de partidos en el grupo',
    body: '"Por resultado" arma cruces 2+2 dentro del grupo de 4 según cómo van saliendo los partidos. "Todos contra todos" hace que cada pareja juegue contra todas las del grupo.',
  },
  'wizard.fixture.teamsAllowed': {
    title: 'Cantidad de equipos',
    body: 'El mínimo es el piso para que el torneo se pueda armar; al llegar al máximo se cierran las inscripciones. Los dos tienen que ser al menos 2, y el mínimo no puede superar al máximo.',
    tip: 'Entre 8 y 16 equipos es un rango cómodo para arrancar.',
  },
  'wizard.fixture.americanFinalBracket': {
    title: 'Cuadro final del americano',
    body: 'Define qué se juega después de las zonas: sin cuadro, solo la final, o cuadros de 4 u 8. Las opciones que ves dependen de cuántos equipos tenga el torneo. Se elige al generar el fixture, no al crear el torneo. Con 6 equipos podés elegir Hexagonal: un solo grupo, todos contra todos, sin eliminatorias.',
  },
  'wizard.team.gender': {
    title: 'Género del torneo',
    body: 'Define quién se puede inscribir: masculino, femenino o mixto. En mixto, la app valida la composición de la pareja al momento de anotarse.',
  },
  'wizard.team.playersPerTeam': {
    title: 'Jugadores por equipo',
    body: 'En pádel son siempre 2 y no se puede cambiar. El campo existe para los deportes que más adelante van a permitir singles.',
  },
  'wizard.team.categories': {
    title: 'Rango de categorías',
    body: 'Acota qué jugadores pueden anotarse: solo entran las parejas cuyos jugadores caen dentro del rango. Si en vez del rango preferís validar el nivel combinado de la pareja, cambiá a suma de categorías.',
    tip: 'Un rango angosto da partidos más parejos; uno amplio llena el torneo más rápido.',
  },
  'wizard.team.forceSum': {
    title: 'Suma de categorías',
    body: 'En vez de mirar la categoría de cada jugador por separado, se valida la suma de las dos. Sirve para permitir parejas desparejas mientras el nivel total quede dentro de lo que definiste.',
  },
  'wizard.team.ageRestriction': {
    title: 'Restricción de edad',
    body: 'Podés poner solo un mínimo, solo un máximo, los dos o ninguno: cada límite se activa por separado con su casilla. El límite que dejás sin activar no restringe nada.',
  },
  'wizard.pricing.paymentType': {
    title: 'Método de pago',
    body: 'Con MercadoPago se cobra online al inscribirse; en efectivo lo cobrás vos en el club; con ambos elige el jugador. Si tu cuenta tiene saldo pendiente, queda forzado a MercadoPago.',
  },
  'wizard.pricing.price': {
    title: 'Precio del torneo',
    body: 'Es lo que paga cada jugador para anotarse. Tiene que ser mayor que la comisión de GetMatch: si no, el wizard no te deja avanzar.',
  },
  'wizard.pricing.fee': {
    title: 'Desglose del precio',
    body: 'Muestra qué se lleva cada comisión y cuánto te queda a vos por inscripción. Sirve para fijar el precio mirando el número que realmente cobrás, no el bruto.',
  },
  'wizard.config.signupDeadline': {
    title: 'Fecha y hora límite para inscribirse',
    body: 'El momento exacto en que se cierra la inscripción, en la hora del club. Además filtra los horarios: en el paso de clubs solo vas a poder elegir bloques que empiecen después de este cierre.',
    tip: 'Dejá 2 o 3 días entre el cierre y el primer partido para armar el fixture con tiempo. Podés moverlo mientras el torneo no tenga fixture, aunque ya haya inscriptos.',
  },
  'wizard.config.matchDuration': {
    title: 'Duración por partido',
    body: 'Cuánto se reserva la cancha por partido. Determina cuántos turnos entran en cada bloque de horario, así que impacta directo en la capacidad del torneo. Mínimo 30 minutos.',
  },
  'wizard.config.minSchedulesRequired': {
    title: 'Mínimo de horarios por jugador',
    body: 'Cuántos horarios tiene que marcar cada equipo al inscribirse. Cuantos más exijas, más fácil te resulta armar el fixture, pero más fricción le ponés al jugador. No aplica al formato americano.',
  },
  'wizard.config.countsForCategoryRanking': {
    title: 'Suma al ranking de categoría',
    body: 'Si está activo, los resultados del torneo dan puntos para el ranking de categoría. Si lo apagás, el torneo se juega igual pero no impacta en esa tabla. El ELO se calcula siempre, actives o no esto.',
  },
  'wizard.config.categoryRankingTarget': {
    title: 'A qué ranking suman los puntos',
    body: 'Con la categoría del jugador, cada uno suma en la suya. Con categoría fija, todos los puntos van a una sola que elegís acá, acotada al rango que definiste en el paso de equipos.',
  },
  'wizard.schedules.clubs': {
    title: 'Clubs del torneo',
    body: 'Elegís en qué clubs se juega. Podés usar más de uno, y los horarios se arman por separado para cada club.',
  },
  'wizard.schedules.blocks': {
    title: 'Bloques de horario',
    body: 'Cada bloque es un rango de días y horas con una cantidad de canchas. La suma de los bloques es la capacidad real del torneo. Solo aparecen los que duran lo mismo que tus partidos y empiezan después del cierre de inscripción.',
    tip: 'Si no ves ningún bloque, revisá la duración de partido y la fecha de cierre del paso anterior.',
  },
  'wizard.schedules.dates': {
    title: 'Fechas del torneo',
    body: 'No hay un campo de fecha de inicio ni de fin en ningún paso: se calculan solas a partir del bloque más temprano y el más tardío que elijas acá.',
  },
  'wizard.summary.review': {
    title: 'Revisión final',
    body: 'Este cartel te dice si el torneo está listo para crearse. Si falta algo, te avisa acá y volvés al paso que corresponda con el stepper de arriba.',
  },
  'wizard.summary.editable': {
    title: 'Qué podés cambiar después',
    body: 'Casi todo se edita desde el detalle del torneo mientras no haya inscriptos, y bastante sigue siendo editable incluso con equipos anotados. El deporte es la excepción: no se cambia nunca.',
  },
  'panel.clubs.list': {
    title: 'Tus clubes',
    body: 'Cada club es una sede con su dirección y sus canchas. Un torneo se juega en uno o varios clubes tuyos, así que este es el primer dato que tenés que cargar. Desde cada club entrás a gestionar sus canchas.',
    tip: 'Cargá todas las canchas reales: de ahí sale cuántos partidos entran por franja horaria.',
  },
  'panel.tournaments.list': {
    title: 'Tus torneos',
    body: 'El listado de todo lo que creaste, con su estado. Desde cada uno entrás al detalle para editarlo, ver los inscriptos, generar el fixture o cargar resultados.',
  },
  'panel.tournaments.create': {
    title: 'Crear un torneo nuevo',
    body: 'Abre el asistente de creación, que te lleva paso a paso por el formato, los equipos, el precio y los horarios. Cada paso tiene su propia guía la primera vez que entrás.',
  },
  'panel.currentAccount.balance': {
    title: 'Tu balance',
    body: 'Muestra lo que debés a GetMatch por las inscripciones cobradas en efectivo: en esos casos la comisión no se descuenta sola, así que queda como saldo negativo. Si te queda deuda, podés cancelarla por transferencia desde acá.',
  },
  'panel.currentAccount.movements': {
    title: 'Historial de movimientos',
    body: 'El detalle de cada comisión que se sumó y cada pago que hiciste. Es donde chequeás por qué el balance quedó como quedó.',
  },
  'panel.profile.data': {
    title: 'Datos de tu cuenta',
    body: 'Tus datos personales y de contacto. El CUIT es obligatorio para operar como manager: sin él, el panel te frena en esta pantalla hasta completarlo.',
  },
  'panel.profile.guides': {
    title: 'Guías para managers nuevos',
    body: 'Con esto prendido, la primera vez que entrás a cada sección aparece un recorrido que te explica qué hace cada cosa. Apagalo cuando ya no lo necesites: la ayuda de cada campo, el ícono de pregunta, sigue estando igual.',
  },
} as const satisfies Record<string, GuideTopic>;

/**
 * Every topic id that exists. Typing consumers with this turns a mistyped
 * `topicId` into a compile error instead of a help icon that silently
 * disappears at runtime.
 */
export type GuideTopicId = keyof typeof GUIDE_TOPICS;
